/**
 * Local Storage Helper for Form Data Persistence
 * Provides utilities to save, retrieve, and clear form drafts in localStorage
 */

const STORAGE_PREFIX = 'limit_setting_draft_';

/**
 * Save form data to local storage with case-specific key
 * @param {string} caseId - The taOnboardingCaseId to use as key
 * @param {object} formData - The form data object to save
 * @returns {boolean} - Success status
 */
export const saveFormDataToLocalStorage = (caseId, formData) => {
  if (!caseId) {
    console.warn('Cannot save to localStorage: caseId is missing');
    return false;
  }

  try {
    // Fields to exclude from local storage (file uploads and large binary data)
    const excludedFields = [
      'passportPhoto',
      'dpn_doc',
      'mt_udc_doc',
      'tf_udc_doc',
      'signature_verification',
      'gst_declaration',
      'name_declaration',
      'dob_declaration',
      'partners_authorization_letter',
      'other_doc',
      'mt_bl_agreement',
      'mt_application_form',
      'mt_sanction_letter'
    ];

    // Filter out excluded fields and large base64 data
    const filteredData = Object.keys(formData).reduce((acc, key) => {
      // Skip excluded fields
      if (excludedFields.includes(key)) {
        return acc;
      }

      const value = formData[key];
      
      // Skip base64 data or very large strings (likely file data)
      if (typeof value === 'string' && (
        value.startsWith('data:') || 
        value.length > 10000
      )) {
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {});

    // Add metadata
    const dataToSave = {
      formData: filteredData,
      timestamp: new Date().toISOString(),
      caseId: caseId
    };

    const key = `${STORAGE_PREFIX}${caseId}`;
    const serializedData = JSON.stringify(dataToSave);
    
    localStorage.setItem(key, serializedData);
    return true;
  } catch (error) {
    // Handle quota exceeded error
    if (error.name === 'QuotaExceededError') {
      console.error('Local storage quota exceeded:', error);
      return false;
    }
    console.error('Error saving to localStorage:', error);
    return false;
  }
};

/**
 * Retrieve form data from local storage for specific case
 * @param {string} caseId - The taOnboardingCaseId to retrieve
 * @returns {object|null} - The saved form data or null if not found/error
 */
export const getFormDataFromLocalStorage = (caseId) => {
  if (!caseId) {
    console.warn('Cannot retrieve from localStorage: caseId is missing');
    return null;
  }

  try {
    const key = `${STORAGE_PREFIX}${caseId}`;
    const serializedData = localStorage.getItem(key);
    
    if (!serializedData) {
      return null;
    }

    const parsedData = JSON.parse(serializedData);
    return parsedData;
  } catch (error) {
    console.error('Error parsing localStorage data:', error);
    // Clear corrupted data
    clearFormDataFromLocalStorage(caseId);
    return null;
  }
};

/**
 * Clear form data from local storage for specific case
 * @param {string} caseId - The taOnboardingCaseId to clear
 * @returns {boolean} - Success status
 */
export const clearFormDataFromLocalStorage = (caseId) => {
  if (!caseId) {
    console.warn('Cannot clear from localStorage: caseId is missing');
    return false;
  }

  try {
    const key = `${STORAGE_PREFIX}${caseId}`;
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
};

/**
 * Get all limit setting draft keys in local storage
 * @returns {Array<string>} - Array of case IDs with saved drafts
 */
export const getAllDraftKeys = () => {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        // Extract caseId from key
        const caseId = key.replace(STORAGE_PREFIX, '');
        keys.push(caseId);
      }
    }
    return keys;
  } catch (error) {
    console.error('Error getting draft keys:', error);
    return [];
  }
};

/**
 * Format timestamp for display
 * @param {string} timestamp - ISO date string
 * @returns {string} - Formatted date/time string
 */
export const formatDraftTimestamp = (timestamp) => {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) {
      return 'just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else if (diffMins < 1440) {
      const hours = Math.floor(diffMins / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      // Format as DD/MM/YYYY HH:MM
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const mins = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${mins}`;
    }
  } catch (error) {
    return 'unknown';
  }
};
